/* ============================================
   Globdrop — Coordinate Math (Pure Functions)
   ============================================ */

import type { BoundingBox, ImageSize, PixelPoint, Point, CoordinateSystem, DistanceUnit } from '../types/scenario.ts';

const KM_PER_MILE = 1.609344;

/**
 * Projects a latitude to Mercator Y coordinate.
 */
function latToY(lat: number): number {
  return Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 180 / 2));
}

/**
 * Inverse Mercator: Y to latitude.
 */
function yToLat(y: number): number {
  return (2 * Math.atan(Math.exp(y)) - Math.PI / 2) * 180 / Math.PI;
}

/**
 * Projects a latitude to Miller Y coordinate.
 */
function latToMillerY(lat: number): number {
  const rad = (lat * Math.PI) / 180;
  return 1.25 * Math.log(Math.tan(Math.PI / 4 + 0.4 * rad));
}

/**
 * Inverse Miller: Y to latitude.
 */
function millerYToLat(y: number): number {
  const rad = 2.5 * (Math.atan(Math.exp(0.8 * y)) - Math.PI / 4);
  return (rad * 180) / Math.PI;
}

/**
 * Projects WGS84 to LAEA (Lambert Azimuthal Equal Area)
 * Standard Europe parameters: lat0=52, lon0=10
 */
function laeaProjection(lon: number, lat: number, lat0Deg = 52, lon0Deg = 10): Point {
  const lat0 = lat0Deg * Math.PI / 180;
  const lon0 = lon0Deg * Math.PI / 180;
  const phi = lat * Math.PI / 180;
  const lambda = lon * Math.PI / 180;

  const cosPhi = Math.cos(phi);
  const sinPhi = Math.sin(phi);
  const cosLambdaDiff = Math.cos(lambda - lon0);
  const sinLambdaDiff = Math.sin(lambda - lon0);

  const k = Math.sqrt(2 / (1 + Math.sin(lat0) * sinPhi + Math.cos(lat0) * cosPhi * cosLambdaDiff));
  
  return {
    x: k * cosPhi * sinLambdaDiff,
    y: k * (Math.cos(lat0) * sinPhi - Math.sin(lat0) * cosPhi * cosLambdaDiff)
  };
}

function inverseLaeaProjection(x: number, y: number, lat0Deg = 52, lon0Deg = 10): Point {
  const lat0 = lat0Deg * Math.PI / 180;
  const lon0 = lon0Deg * Math.PI / 180;
  
  const rho = Math.sqrt(x * x + y * y);
  if (rho === 0) return { x: lon0Deg, y: lat0Deg };
  
  const c = 2 * Math.asin(rho / 2);
  const cosC = Math.cos(c);
  const sinC = Math.sin(c);
  
  const lat = Math.asin(cosC * Math.sin(lat0) + (y * sinC * Math.cos(lat0)) / rho) * 180 / Math.PI;
  const lon = (lon0 + Math.atan2(x * sinC, rho * Math.cos(lat0) * cosC - y * Math.sin(lat0) * sinC)) * 180 / Math.PI;
  
  return { x: lon, y: lat };
}

/**
 * Translates a map coordinate to a pixel position on the image.
 *
 * Works with any coordinate system (UTM, WGS84, arbitrary grid)
 * as long as the bounding box uses the same system.
 */

/**
 * Projects a WGS84 point to Albers Equal Area Conic (USA)
 * Standard parallels: 29.5, 45.5. Central meridian: -98.5. Latitude of origin: 38.
 */
function albersProjection(lon: number, lat: number) {
  const phi1 = 29.5 * Math.PI / 180;
  const phi2 = 45.5 * Math.PI / 180;
  const phi0 = 38 * Math.PI / 180;
  const lambda0 = -98.5 * Math.PI / 180;

  const n = (Math.sin(phi1) + Math.sin(phi2)) / 2;
  const C = Math.cos(phi1) * Math.cos(phi1) + 2 * n * Math.sin(phi1);
  const rho0 = Math.sqrt(C - 2 * n * Math.sin(phi0)) / n;

  const phi = lat * Math.PI / 180;
  const lambda = lon * Math.PI / 180;

  const rho = Math.sqrt(C - 2 * n * Math.sin(phi)) / n;
  const theta = n * (lambda - lambda0);

  const x = rho * Math.sin(theta);
  const y = rho0 - rho * Math.cos(theta);

  return { x, y };
}

/**
 * Inverse Albers Equal Area Conic (USA)
 */
function inverseAlbersProjection(x: number, y: number) {
  const phi1 = 29.5 * Math.PI / 180;
  const phi2 = 45.5 * Math.PI / 180;
  const phi0 = 38 * Math.PI / 180;
  const lambda0 = -98.5 * Math.PI / 180;

  const n = (Math.sin(phi1) + Math.sin(phi2)) / 2;
  const C = Math.cos(phi1) * Math.cos(phi1) + 2 * n * Math.sin(phi1);
  const rho0 = Math.sqrt(C - 2 * n * Math.sin(phi0)) / n;

  const rho = Math.sqrt(x * x + Math.pow(rho0 - y, 2));
  const theta = Math.atan2(x, rho0 - y);

  const phi = Math.asin((C - rho * rho * n * n) / (2 * n));
  const lambda = lambda0 + theta / n;

  return { x: lambda * 180 / Math.PI, y: phi * 180 / Math.PI };
}

export function coordToPixel(
  coord: Point,
  boundingBox: BoundingBox,
  imageSize: ImageSize,
  system: CoordinateSystem = 'WGS84'
): PixelPoint {
  const { width, height } = imageSize;
  const { topLeft, bottomRight } = boundingBox;

  const rangeX = bottomRight.x - topLeft.x;
  const scaleX = width / rangeX;
  
  if (system === 'WGS84') {
    const topY = latToY(topLeft.y);
    const bottomY = latToY(bottomRight.y);
    const rangeY = bottomY - topY;
    const scaleY = height / rangeY;

    return {
      px: (coord.x - topLeft.x) * scaleX,
      py: (latToY(coord.y) - topY) * scaleY,
    };
  } else if (system === 'Miller') {
    const topY = latToMillerY(topLeft.y);
    const bottomY = latToMillerY(bottomRight.y);
    const rangeY = bottomY - topY;
    const scaleY = height / rangeY;

    return {
      px: (coord.x - topLeft.x) * scaleX,
      py: (latToMillerY(coord.y) - topY) * scaleY,
    };
    } else if (system === 'AlbersUSA') {
    const projected = albersProjection(coord.x, coord.y);
    // Actually, boundingBox provides standard lat/lon. Let's project the bounds.
    const tl = albersProjection(topLeft.x, topLeft.y);
    const br = albersProjection(bottomRight.x, bottomRight.y);
    
    const rangeXProj = br.x - tl.x;
    const rangeYProj = br.y - tl.y;
    
    return {
      px: (projected.x - tl.x) * (width / rangeXProj),
      py: (projected.y - tl.y) * (height / rangeYProj),
    };
  } else if (system.startsWith('LAEA')) {
    let lat0 = 52;
    let lon0 = 10;
    if (system === 'LAEA-USA') {
      lat0 = 45;
      lon0 = -100;
    }
    const projected = laeaProjection(coord.x, coord.y, lat0, lon0);
    const tl = laeaProjection(topLeft.x, topLeft.y, lat0, lon0);
    const br = laeaProjection(bottomRight.x, bottomRight.y, lat0, lon0);
    
    const rangeXProj = br.x - tl.x;
    const rangeYProj = br.y - tl.y;
    
    return {
      px: (projected.x - tl.x) * (width / rangeXProj),
      py: (projected.y - tl.y) * (height / rangeYProj),
    };
  } else {
    // Linear (UTM or custom grid)
    const rangeY = bottomRight.y - topLeft.y;
    const scaleY = height / rangeY;

    return {
      px: (coord.x - topLeft.x) * scaleX,
      py: (coord.y - topLeft.y) * scaleY,
    };
  }
}

/**
 * Translates a pixel position on the image to a map coordinate.
 */
export function pixelToCoord(
  pixel: PixelPoint,
  boundingBox: BoundingBox,
  imageSize: ImageSize,
  system: CoordinateSystem = 'WGS84'
): Point {
  const { width, height } = imageSize;
  const { topLeft, bottomRight } = boundingBox;

  const rangeX = bottomRight.x - topLeft.x;
  const x = (pixel.px / width) * rangeX + topLeft.x;
  
  if (system === 'WGS84') {
    const topY = latToY(topLeft.y);
    const bottomY = latToY(bottomRight.y);
    const rangeY = bottomY - topY;
    
    const mercY = (pixel.py / height) * rangeY + topY;
    const y = yToLat(mercY);
    return { x, y };
  } else if (system === 'Miller') {
    const topY = latToMillerY(topLeft.y);
    const bottomY = latToMillerY(bottomRight.y);
    const rangeY = bottomY - topY;
    
    const millY = (pixel.py / height) * rangeY + topY;
    const y = millerYToLat(millY);
    return { x, y };
  } else if (system === 'AlbersUSA') {
    const tl = albersProjection(topLeft.x, topLeft.y);
    const br = albersProjection(bottomRight.x, bottomRight.y);
    
    const rangeXProj = br.x - tl.x;
    const rangeYProj = br.y - tl.y;
    
    const projX = (pixel.px / width) * rangeXProj + tl.x;
    const projY = (pixel.py / height) * rangeYProj + tl.y;
    return inverseAlbersProjection(projX, projY);
  } else if (system.startsWith('LAEA')) {
    let lat0 = 52;
    let lon0 = 10;
    if (system === 'LAEA-USA') {
      lat0 = 45;
      lon0 = -100;
    }
    const tl = laeaProjection(topLeft.x, topLeft.y, lat0, lon0);
    const br = laeaProjection(bottomRight.x, bottomRight.y, lat0, lon0);
    
    const rangeXProj = br.x - tl.x;
    const rangeYProj = br.y - tl.y;
    
    const projX = (pixel.px / width) * rangeXProj + tl.x;
    const projY = (pixel.py / height) * rangeYProj + tl.y;
    return inverseLaeaProjection(projX, projY, lat0, lon0);
  } else {
    // Linear
    const rangeY = bottomRight.y - topLeft.y;
    const y = (pixel.py / height) * rangeY + topLeft.y;
    return { x, y };
  }
}

/**
 * Calculates the Euclidean distance between two pixel points.
 */
export function pixelDistance(a: PixelPoint, b: PixelPoint): number {
  return Math.sqrt(
    Math.pow(a.px - b.px, 2) + Math.pow(a.py - b.py, 2)
  );
}

/**
 * Converts a pixel distance to real-world distance.
 *
 * @param pixelDist - Distance in pixels
 * @param mapWidthPx - Total width of the map image in pixels
 * @param mapRealWidth - Real-world width of the map (in km or mi)
 * @returns Distance in real-world units
 */
export function pixelDistToReal(
  pixelDist: number,
  mapWidthPx: number,
  mapRealWidth: number
): number {
  const kmPerPixel = mapRealWidth / mapWidthPx;
  return pixelDist * kmPerPixel;
}

/**
 * Calculates the Great Circle distance (Haversine formula) between two WGS84 points.
 * @returns distance in km
 */
export function haversineDistance(p1: Point, p2: Point): number {
  const R = 6371; // Earth radius in km
  const dLat = ((p2.y - p1.y) * Math.PI) / 180;
  const dLon = ((p2.x - p1.x) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((p1.y * Math.PI) / 180) *
      Math.cos((p2.y * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * True when the system's coordinates are longitude and latitude, so a click
 * can be turned back into a point on the globe.
 */
export function isGeographic(system: CoordinateSystem): boolean {
  return system === 'WGS84' || system === 'Miller' || system === 'AlbersUSA' || system.startsWith('LAEA');
}

/**
 * Full pipeline: calculate real-world distance between user click and target,
 * in the scenario's unit.
 *
 * Maps of the real world use the great-circle distance. A single pixel scale
 * is wrong on them: on the world maps it made misses about 30% short at the
 * equator and 40% long at 60°. UTM (already in metres) and the fictional
 * Linear maps keep the pixel scale from mapRealWidth.
 */
export function calculateDistance(
  userPixel: PixelPoint,
  targetCoord: Point,
  boundingBox: BoundingBox,
  imageSize: ImageSize,
  mapRealWidth: number,
  coordSystem: CoordinateSystem = 'WGS84',
  distanceUnit: DistanceUnit = 'km'
): number {
  if (isGeographic(coordSystem)) {
    const userCoord = pixelToCoord(userPixel, boundingBox, imageSize, coordSystem);
    const km = haversineDistance(userCoord, targetCoord);
    return distanceUnit === 'mi' ? km / KM_PER_MILE : km;
  }

  // UTM and Linear: one scale for the whole image
  const targetPixel = coordToPixel(targetCoord, boundingBox, imageSize, coordSystem);
  const pxDist = pixelDistance(userPixel, targetPixel);
  return pixelDistToReal(pxDist, imageSize.width, mapRealWidth);
}
